import { PageDataTable } from "components/PageDataTable/PageDataTable";
import { ToastContext } from "context/ToastContext";
import { Button } from "primereact/button";
import { Chart } from "primereact/chart";
import { Column } from "primereact/column";
import { DataTablePFSEvent } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { SelectButton } from "primereact/selectbutton";
import { Tag } from "primereact/tag";
import { useContext, useMemo, useState } from "react";
import {
  useGetFinixWebhooksQuery,
  useGetFinixWebhookStatsQuery,
  useRetryFinixWebhookMutation,
} from "store/queries/finixOps";
import {
  FinixWebhookStatus,
  IFinixWebhookItem,
  IFinixWebhookStatusCounts,
} from "types/FinixOps";

import { apiErrorMessage, formatDateTime } from "../../utils/hstImports";

const PAGE_SIZE = 25;

const STATUSES: FinixWebhookStatus[] = [
  "PENDING",
  "PROCESSED",
  "FAILED",
  "SKIPPED",
];

const STATUS_LABEL: Record<FinixWebhookStatus, string> = {
  PENDING: "Pending",
  PROCESSED: "Processed",
  FAILED: "Failed",
  SKIPPED: "Skipped",
};

const STATUS_SEVERITY: Record<
  FinixWebhookStatus,
  "success" | "danger" | "warning" | "info"
> = {
  PENDING: "info",
  PROCESSED: "success",
  FAILED: "danger",
  SKIPPED: "warning",
};

const STATUS_FILTER_OPTIONS = [
  // `null`, not `undefined`: PrimeReact passes the option object for an undefined value (dev QA 10-08).
  { label: "All statuses", value: null },
  ...STATUSES.map((s) => ({ label: STATUS_LABEL[s], value: s })),
];

const RANGE_OPTIONS = [
  { label: "30 days", value: 30 },
  { label: "90 days", value: 90 },
];

// Received / processed / failed / skipped, as the ticket lists them.
const CARD_KEYS: (keyof IFinixWebhookStatusCounts)[] = [
  "received",
  "processed",
  "failed",
  "skipped",
];

const CARD_LABEL: Record<keyof IFinixWebhookStatusCounts, string> = {
  received: "Received",
  processed: "Processed",
  failed: "Failed",
  skipped: "Skipped",
  pending: "Pending",
};

/** The bar chart's data: one bar pair per UTC day, received vs processed. */
export const buildWebhookChartData = (
  points: { date: string; received: number; processed: number }[]
) => ({
  labels: points.map((p) => p.date.slice(5)),
  datasets: [
    {
      type: "bar",
      label: "Received",
      backgroundColor: "#7C71FD",
      data: points.map((p) => p.received),
    },
    {
      type: "bar",
      label: "Processed",
      backgroundColor: "#0ECFA9",
      data: points.map((p) => p.processed),
    },
  ],
});

const CHART_OPTIONS = {
  maintainAspectRatio: false,
  maxBarThickness: 20,
  plugins: {
    legend: { align: "start" },
    tooltip: { mode: "index", intersect: false },
  },
  scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
};

const statusBody = (w: IFinixWebhookItem) => (
  <Tag value={STATUS_LABEL[w.status]} severity={STATUS_SEVERITY[w.status]} />
);

const receivedBody = (w: IFinixWebhookItem) => formatDateTime(w.createdAt);

const processedBody = (w: IFinixWebhookItem) => formatDateTime(w.processedAt);

const errorBody = (w: IFinixWebhookItem) =>
  w.lastError ? (
    <span
      className={w.status === "FAILED" ? "text-red-600" : "text-600"}
      style={{ wordBreak: "break-word" }}
    >
      {w.lastError}
    </span>
  ) : null;

function CountCards({
  title,
  counts,
  loading,
  testIdPrefix,
}: {
  title: string;
  counts: IFinixWebhookStatusCounts | undefined;
  loading: boolean;
  testIdPrefix: string;
}) {
  return (
    <div className="flex flex-column gap-2">
      <small className="text-600">{title}</small>
      <div className="grid">
        {CARD_KEYS.map((key) => (
          <div key={key} className="col-6 md:col-3">
            <div
              className="surface-ground border-round p-3 text-center"
              data-testid={`${testIdPrefix}-${key}`}
            >
              <div className="text-2xl font-bold">
                {loading && !counts ? "…" : counts?.[key] ?? 0}
              </div>
              <div className="text-600 text-sm">{CARD_LABEL[key]}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function FinixWebhooksSection() {
  const toastRef = useContext(ToastContext);
  const [days, setDays] = useState(30);
  const [status, setStatus] = useState<FinixWebhookStatus | undefined>(
    undefined
  );
  const [page, setPage] = useState(1);
  const [retryingId, setRetryingId] = useState<number | null>(null);

  const { data: statsData, isFetching: statsLoading } =
    useGetFinixWebhookStatsQuery(days);
  const { data, isFetching } = useGetFinixWebhooksQuery({
    status,
    page,
    pageSize: PAGE_SIZE,
  });
  const [retryWebhook] = useRetryFinixWebhookMutation();

  const stats = statsData?.data;
  const chartData = useMemo(
    () => buildWebhookChartData(stats?.series.points || []),
    [stats]
  );
  const list = data?.data;

  const onPage = (event: DataTablePFSEvent) => {
    setPage(Math.floor(event.first / event.rows) + 1);
  };

  const onRetry = async (webhook: IFinixWebhookItem) => {
    setRetryingId(webhook.id);
    try {
      await retryWebhook(webhook.id).unwrap();
      toastRef?.current?.show({
        severity: "success",
        summary: "Retry queued",
        detail:
          "The webhook is pending again; the webhook job picks it up within a couple of minutes.",
      });
    } catch (error) {
      toastRef?.current?.show({
        severity: "error",
        summary: "Could not retry",
        detail: apiErrorMessage(error).message,
      });
    } finally {
      setRetryingId(null);
    }
  };

  const actionsBody = (w: IFinixWebhookItem) =>
    w.status === "FAILED" ? (
      <Button
        label="Retry"
        icon="pi pi-refresh"
        className="p-button-text p-button-sm"
        loading={retryingId === w.id}
        disabled={retryingId !== null}
        onClick={() => onRetry(w)}
        data-testid={`finix-webhook-retry-${w.id}`}
      />
    ) : null;

  return (
    <div className="flex flex-column gap-3">
      <div className="grid">
        <div className="col-12 lg:col-6">
          <CountCards
            title="Last 7 days"
            counts={stats?.last7Days}
            loading={statsLoading}
            testIdPrefix="finix-webhooks-7d"
          />
        </div>
        <div className="col-12 lg:col-6">
          <CountCards
            title="Last 30 days"
            counts={stats?.last30Days}
            loading={statsLoading}
            testIdPrefix="finix-webhooks-30d"
          />
        </div>
      </div>

      <div className="flex flex-column gap-2">
        <div className="flex flex-wrap align-items-center justify-content-between gap-2">
          <span className="font-semibold">Received and processed per day</span>
          <SelectButton
            value={days}
            options={RANGE_OPTIONS}
            onChange={(e) => e.value && setDays(e.value)}
            aria-label="Chart range"
          />
        </div>
        <div style={{ height: "260px" }}>
          <Chart
            type="bar"
            data={chartData}
            options={CHART_OPTIONS}
            style={{ height: "100%" }}
          />
        </div>
        <small className="text-600">
          UTC days. Received counts every webhook by when it arrived; processed
          counts the ones marked processed, by when.
        </small>
      </div>

      <div className="flex flex-wrap align-items-center justify-content-between gap-3">
        <Dropdown
          value={status}
          options={STATUS_FILTER_OPTIONS}
          onChange={(e) => {
            setStatus(e.value || undefined);
            setPage(1);
          }}
          placeholder="All statuses"
          style={{ minWidth: "200px" }}
          aria-label="Filter webhooks by status"
        />
        <small className="text-600">
          Pending and failed webhooks first. Retry puts a failed webhook back in
          the queue.
        </small>
      </div>

      <PageDataTable
        value={list?.items || []}
        dataKey="id"
        lazy
        paginator
        first={(page - 1) * PAGE_SIZE}
        rows={PAGE_SIZE}
        totalRecords={list?.total || 0}
        onPage={onPage}
        loading={isFetching}
        emptyMessage="No Finix webhooks."
      >
        <Column header="Received" body={receivedBody} />
        <Column header="Entity" field="entityType" />
        <Column header="Event" field="eventType" />
        <Column header="Entity id" field="entityId" />
        <Column header="Status" body={statusBody} />
        <Column header="Attempts" field="attempts" />
        <Column header="Processed" body={processedBody} />
        <Column header="Error" body={errorBody} />
        <Column header="" body={actionsBody} style={{ width: "7rem" }} />
      </PageDataTable>
    </div>
  );
}
